import React from "react";
import simplePageImg from "@/assets/img/simplepage.PNG";
import productPageImg from "@/assets/img/product_page.PNG";
import categoryPageImg from "@/assets/img/category_page.PNG";
import subcategoryPageImg from "@/assets/img/subcategory_page.PNG";
import postImg from "@/assets/img/post.PNG";
import authorImg from "@/assets/img/author.PNG";
import {
  Card,
  CardBody,
  CardImg,
  CardTitle,
  ListGroupItem,
  ListGroup,
  Row,
  Col,
  Container,
  FormGroup,
  Input,
  ListGroupItemHeading,
  ListGroupItemText,
  Label,
  Badge,
  InputGroup,
  InputGroupText,
  Pagination,
  PaginationItem,
  PaginationLink,
  CardHeader,
} from "reactstrap";
import { GenericPagination } from "@/utils/GenericPagination";
import type { RootState } from "@/services/store";

/** Which page-type card is selected; exactly one is true once the editor picks. */
export type PageTypeChecker = {
  simple_page: boolean;
  category: boolean;
  subcategory: boolean;
  product: boolean;
  posts: boolean;
  authors: boolean;
};

type InputChange = (e: React.ChangeEvent<HTMLInputElement>) => void;

/**
 * The picker lists (categories, sub-categories, products, posts, authors) are
 * `any[]` as they were in the JSDoc: they come from store slices typed `any[]`
 * (categories.pageTypeSubcategories, blog.posts) and the view reads each
 * entity's own fields.
 */
export type PageTypesCreateViewProps = {
  checker: PageTypeChecker;
  /** An entity id, a path, or null: compared and tested for truthiness only. */
  requiresChecker: unknown;
  pathSimplePage: string;
  pathEntity: string;
  derivedPathNotice: string;
  radioGroupValue: string;
  blogAvailability: boolean;
  postsAvailability: boolean;
  authors: any[];
  CurrentSite: RootState["sites"]["site"];
  CategoryList: any[];
  SubCategoriesList: any[];
  ProductsList: any[];

  displayedProducts: any[];
  totalProductPages: number;
  currentPageProducts: number;
  displayedSubcategories: any[];
  totalSubcategoryPages: number;
  currentPageSubcats: number;

  slicedPosts: any[];
  currentPage: number;
  pagesCount: number;

  isValidationAttempted?: boolean;
  isPageTypeSelected: boolean;

  selectType: (name: keyof PageTypeChecker) => void;
  onChangeSimplePageInputHandler: InputChange;
  onChangeEntityPathHandler: InputChange;
  onChangeRadioInputHandler: InputChange;
  onClickCategoryHandler: (category: { _id?: unknown }) => void;
  onClickSubCategoryHandler: (subCategory: { _id?: unknown }) => void;
  onClickproductsHandler: (productWrapper: { product: { _id?: unknown } }) => void;
  onClickPostHandler: (post: { _id?: unknown }) => void;
  onClickAuthorHandler: (author: { _id?: unknown }) => void;

  searchPost: InputChange;
  searchProduct: InputChange;
  searchSubcategory: InputChange;

  handlePostsPageClick: (e: { preventDefault?: () => void } | undefined, index: number) => void;
  handleProductsPaginationClick: (pageIndex: number) => void;
  handleSubcategoriesPaginationClick: (pageIndex: number) => void;
};

// Pure presentational component
const PageTypesCreateView = (props: PageTypesCreateViewProps) => {
  const {
    // state/flags
    checker,
    requiresChecker,
    pathSimplePage,
    pathEntity,
    derivedPathNotice,
    radioGroupValue,
    blogAvailability,
    postsAvailability,
    authors,
    CurrentSite,
    CategoryList,
    SubCategoriesList,

    // computed
    displayedProducts,
    totalProductPages,
    currentPageProducts,
    displayedSubcategories,
    totalSubcategoryPages,
    currentPageSubcats,

    // posts slice
    slicedPosts,
    currentPage,
    pagesCount,

    // validations
    isValidationAttempted,
    isPageTypeSelected,

    // handlers
    selectType,
    onChangeSimplePageInputHandler,
    onChangeEntityPathHandler,
    onChangeRadioInputHandler,
    onClickCategoryHandler,
    onClickSubCategoryHandler,
    onClickproductsHandler,
    onClickPostHandler,
    onClickAuthorHandler,

    // search
    searchPost,
    searchProduct,
    searchSubcategory,

    // pagination
    handlePostsPageClick,
    handleProductsPaginationClick,
    handleSubcategoriesPaginationClick,
  } = props;

  return (
    <Container>
      <Row>
        <Col lg="12">
          <div className="card-group">
            {/* SIMPLE PAGE */}
            <Col lg="2">
              <Card className="card-lift--hover shadow border-0">
                <CardImg alt="Custom Page" src={simplePageImg} top />
                <CardBody>
                  <CardTitle>Custom Page</CardTitle>
                  {!checker.simple_page ? (
                    <button
                      type="button"
                      className="div-icon-animation"
                      data-testid="simple_page"
                      aria-label="Select Custom Page"
                      onClick={() => selectType("simple_page")}
                    >
                      <i className="ni ni-check-bold fa-3x text-muted" />
                    </button>
                  ) : (
                    <div>
                      <i className="ni ni-check-bold fa-3x text-green" />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* PRODUCT PAGE */}
            <Col lg="2">
              <Card className="card-lift--hover shadow border-0">
                <CardImg alt="Product Page" src={productPageImg} top />
                <CardBody>
                  <CardTitle>Product Page</CardTitle>
                  {!checker.product ? (
                    <button
                      type="button"
                      className="div-icon-animation"
                      data-testid="product"
                      aria-label="Select Product Page"
                      onClick={() => selectType("product")}
                    >
                      <i className="ni ni-check-bold fa-3x text-muted" />
                    </button>
                  ) : (
                    <div>
                      <i className="ni ni-check-bold fa-3x text-green" />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* INTRO (CATEGORY) */}
            <Col lg="2">
              <Card className="card-lift--hover shadow border-0">
                <CardImg alt="Intro Page" src={categoryPageImg} top />
                <CardBody>
                  <CardTitle>Intro Page</CardTitle>
                  {!checker.category ? (
                    <button
                      type="button"
                      className="div-icon-animation"
                      data-testid="category"
                      aria-label="Select Intro Page"
                      onClick={() => selectType("category")}
                    >
                      <i className="ni ni-check-bold fa-3x text-muted" />
                    </button>
                  ) : (
                    <div>
                      <i className="ni ni-check-bold fa-3x text-green" />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* LISTING (SUBCATEGORY) */}
            <Col lg="2">
              <Card className="card-lift--hover shadow border-0">
                <CardImg alt="Listing Page" src={subcategoryPageImg} top />
                <CardBody>
                  <CardTitle>Listing Page</CardTitle>
                  {!checker.subcategory ? (
                    <button
                      type="button"
                      className="div-icon-animation"
                      data-testid="subcategory"
                      aria-label="Select Listing Page"
                      onClick={() => selectType("subcategory")}
                    >
                      <i className="ni ni-check-bold fa-3x text-muted" />
                    </button>
                  ) : (
                    <div>
                      <i className="ni ni-check-bold fa-3x text-green" />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* BLOG ARTICLE */}
            <Col lg="2">
              <Card className="card-lift--hover shadow border-0">
                <CardImg alt="Blog Article Page" src={postImg} top />
                <CardBody>
                  <CardTitle>Blog Article Page</CardTitle>
                  {!checker.posts ? (
                    <button
                      type="button"
                      className="div-icon-animation"
                      data-testid="posts"
                      aria-label="Select Blog Article Page"
                      onClick={() => selectType("posts")}
                    >
                      <i className="ni ni-check-bold fa-3x text-muted" />
                    </button>
                  ) : (
                    <div>
                      <i className="ni ni-check-bold fa-3x text-green" />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* BLOG AUTHOR */}
            <Col lg="2">
              <Card className="card-lift--hover shadow border-0">
                <CardImg alt="Blog Author Page" src={authorImg} top />
                <CardBody>
                  <CardTitle>Blog Author Page</CardTitle>
                  {!checker.authors ? (
                    <button
                      type="button"
                      className="div-icon-animation"
                      data-testid="authors"
                      aria-label="Select Blog Author Page"
                      onClick={() => selectType("authors")}
                    >
                      <i className="ni ni-check-bold fa-3x text-muted" />
                    </button>
                  ) : (
                    <div>
                      <i className="ni ni-check-bold fa-3x text-green" />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </div>
        </Col>
      </Row>

      {!isPageTypeSelected && (
        <div className="text-danger mb-2">
          {isValidationAttempted && "Please select a page type"}
        </div>
      )}

      {/* SIMPLE PAGE CONFIG */}
      {checker.simple_page && (
        <FormGroup className="row">
          <Label className="form-control-label" htmlFor="example4cols1Input" md="3">
            Specify Routing path
          </Label>
          <Col md="5">
            <Input
              id="example4cols1Input"
              placeholder="example : Home,Redirect,Author ..."
              type="text"
              disabled={radioGroupValue !== "default"}
              onChange={onChangeSimplePageInputHandler}
              value={pathSimplePage}
            />
            <Row>
              <Col md="3">
                <div className="custom-control custom-checkbox">
                  <input
                    className="custom-control-input"
                    id="default_page"
                    type="checkbox"
                    value="default"
                    checked={radioGroupValue === "default"}
                    onChange={onChangeRadioInputHandler}
                  />
                  <label className="custom-control-label" htmlFor="default_page">
                    Default
                  </label>
                </div>
              </Col>
              <Col md="3">
                <div className="custom-control custom-checkbox">
                  <input
                    className="custom-control-input"
                    id="error_page"
                    type="checkbox"
                    value="error"
                    checked={radioGroupValue === "error"}
                    onChange={onChangeRadioInputHandler}
                  />
                  <label className="custom-control-label" htmlFor="error_page">
                    Error page
                  </label>
                </div>
              </Col>
              <Col md="3">
                <div className="custom-control custom-checkbox">
                  <input
                    className="custom-control-input"
                    id="maintenance_page"
                    type="checkbox"
                    value="maintenance"
                    checked={radioGroupValue === "maintenance"}
                    onChange={onChangeRadioInputHandler}
                  />
                  <label className="custom-control-label" htmlFor="maintenance_page">
                    Maintenance page
                  </label>
                </div>
              </Col>
              <Col md="3">
                <div className="custom-control custom-checkbox">
                  <input
                    className="custom-control-input"
                    id="redirect_page"
                    type="checkbox"
                    value="redirect"
                    checked={radioGroupValue === "redirect"}
                    onChange={onChangeRadioInputHandler}
                  />
                  <label className="custom-control-label" htmlFor="redirect_page">
                    Redirect page
                  </label>
                </div>
              </Col>
            </Row>
          </Col>
        </FormGroup>
      )}

      {/* CATEGORY PICKER */}
      {checker.category && (
        <>
          <Row>
            <h4 className="h3 text-info text-uppercase">Select Page Category :</h4>
          </Row>
          <Row>
            {CategoryList.map((category, index) => (
              <Col md="6" xl="3" key={category._id || index}>
                <Card className="card-stats">
                  <CardBody>
                    <Row>
                      <div className="col">
                        <CardTitle tag="h5" className="text-uppercase text-default mb-0">
                          {category.name}
                        </CardTitle>
                      </div>
                      <Col className="col-auto">
                        <div className="icon icon-shape bg-gradient-red text-white rounded-circle shadow">
                          <i className="ni ni-active-40" />
                        </div>
                      </Col>
                    </Row>
                    <ListGroup flush>
                      <ListGroupItem>
                        <ListGroupItemHeading>
                          Sub Categories{" "}
                          <Badge color="primary" pill>
                            {category.subcategories.length}
                          </Badge>
                        </ListGroupItemHeading>
                        {category.subcategories.map((sc, i) => (
                          <ListGroupItemText key={sc._id || i}>{sc.name}</ListGroupItemText>
                        ))}
                      </ListGroupItem>

                      <ListGroupItem>
                        <ListGroupItemHeading>Products</ListGroupItemHeading>
                        {category.subcategories.map((sc) =>
                          // A subcategory from the site document has no `products`
                          // (SCRUM-1737); each type-options wrapper names its own
                          // subcategory instead. Only linked products are shown.
                          props.ProductsList?.filter(
                            (p) =>
                              String(p.subcategory) === String(sc._id) &&
                              (p.product?.linked?.length || 0) > 0
                          ).map((pr) =>
                            pr.product ? (
                              <ListGroupItemText key={pr._id}>
                                {pr.product.title}
                              </ListGroupItemText>
                            ) : null
                          )
                        )}
                      </ListGroupItem>
                    </ListGroup>

                    {requiresChecker !== category._id ? (
                      <button
                        type="button"
                        className="div-icon-animation"
                        data-testid={category._id}
                        aria-label={`Select ${category.name}`}
                        onClick={() => onClickCategoryHandler(category)}
                      >
                        <i className="ni ni-check-bold fa-3x text-muted" />
                      </button>
                    ) : (
                      <div>
                        <i className="ni ni-check-bold fa-3x text-green" />
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          {checker.category && !requiresChecker && (
            <div className="text-danger mb-2">
              {isValidationAttempted && "Please select a category."}
            </div>
          )}
        </>
      )}

      {/* SUBCATEGORY PICKER */}
      {checker.subcategory && (
        <>
          <Row>
            <h4 className="h3 text-info text-uppercase">Select Page Sub Category :</h4>
          </Row>

          <Row>
            <FormGroup className="navbar-search form-inline pt-3 navbar-search-dark ">
              <FormGroup className="mb-3">
                <InputGroup className="input-group-alternative input-group-merge">
                  <InputGroupText>
                    <i className="fas fa-search" />
                  </InputGroupText>
                  <Input placeholder="Search for Subcategories.." type="text" onChange={searchSubcategory} />
                </InputGroup>
              </FormGroup>
            </FormGroup>
          </Row>

          <Row>
            {displayedSubcategories.map((sub_category, index) => (
              <Col md="6" xl="3" key={sub_category._id || index}>
                <Card className="card-stats">
                  <CardBody>
                    <Row>
                      <div className="col">
                        <CardTitle tag="h5" className="text-uppercase text-default mb-0">
                          {sub_category.name}
                        </CardTitle>
                      </div>
                      <Col className="col-auto">
                        <div className="icon icon-shape bg-gradient-orange text-white rounded-circle shadow">
                          <i className="ni ni-chart-pie-35" />
                        </div>
                      </Col>
                    </Row>

                    <ListGroup flush>
                      <ListGroupItem>
                        <ListGroupItemHeading>Category</ListGroupItemHeading>
                        <ListGroupItemText>{sub_category.category.name}</ListGroupItemText>
                      </ListGroupItem>

                      {/*
                        SCRUM-1452: the count only, never the product titles. The
                        card used to render one row per product, and a sub-category
                        here carries up to 439 of them (bancompara.pt, measured
                        2026-09-06), which pushed the select button and the pager
                        far below the fold. The number is what an editor needs to
                        judge whether the listing will have content.
                      */}
                      <ListGroupItem>
                        <ListGroupItemHeading>
                          Products{" "}
                          <Badge color="primary" pill>
                            {
                              sub_category.products.filter(
                                (pr) =>
                                  pr.product !== null &&
                                  pr.product.country_code === CurrentSite.country_code &&
                                  (pr.product.linked?.length || 0) > 0
                              ).length
                            }
                          </Badge>
                        </ListGroupItemHeading>
                      </ListGroupItem>
                    </ListGroup>

                    {requiresChecker !== sub_category._id ? (
                      <button
                        type="button"
                        className="div-icon-animation"
                        data-testid={sub_category._id}
                        aria-label={`Select ${sub_category.name}`}
                        onClick={() => onClickSubCategoryHandler(sub_category)}
                      >
                        <i className="ni ni-check-bold fa-3x text-muted" />
                      </button>
                    ) : (
                      <div>
                        <i className="ni ni-check-bold fa-3x text-green" />
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          <GenericPagination
            currentPage={currentPageSubcats}
            totalPages={totalSubcategoryPages}
            onPageChange={handleSubcategoriesPaginationClick}
          />

          {checker.subcategory && !requiresChecker && (
            <div className="text-danger mb-2">
              {isValidationAttempted && "Please select a subcategory."}
            </div>
          )}
        </>
      )}

      {/* ENTITY ROUTING PATH (SCRUM-1575): category and sub_category pages used
          to take a derived path with nothing on the screen to correct it. The
          field shows the derived value once an entity is picked and is editable;
          emptying it restores the derived value. */}
      {(checker.category || checker.subcategory) && !!requiresChecker && (
        <FormGroup className="row">
          <Label className="form-control-label" htmlFor="entityRoutingPathInput" md="3">
            Specify Routing path
          </Label>
          <Col md="5">
            <Input
              id="entityRoutingPathInput"
              placeholder="example : accounts/personal-accounts"
              type="text"
              onChange={onChangeEntityPathHandler}
              value={pathEntity}
            />
            {/* SCRUM-1704: the derived value came from `label[]`, not from the
                site's TaxonomyOverlay row — say so where the editor can act. */}
            {!!derivedPathNotice && (
              <small className="form-text text-warning" data-testid="entity-path-notice">
                {derivedPathNotice}
              </small>
            )}
          </Col>
        </FormGroup>
      )}

      {/* PRODUCT PICKER */}
      {checker.product && (
        <>
          <Row>
            <h4 className="h3 text-info text-uppercase">Select Page product:</h4>
          </Row>

          <Row>
            <FormGroup className="navbar-search form-inline pt-3 navbar-search-dark ">
              <FormGroup className="mb-3">
                <InputGroup className="input-group-alternative input-group-merge">
                  <InputGroupText>
                    <i className="fas fa-search" />
                  </InputGroupText>
                  <Input placeholder="Search for Products.." type="text" onChange={searchProduct} />
                </InputGroup>
              </FormGroup>
            </FormGroup>
          </Row>

          <Row>
            {displayedProducts.map((pp, index) => (
              <Col lg="3" key={pp._id || index} style={{ marginTop: 15 }}>
                <Card>
                  <CardImg
                    alt={pp.product.title}
                    src={
                      "https://res.cloudinary.com/netcapitalcms/image/upload/" +
                        pp.product.country_code +
                        "/product/" +
                        (pp.product.productImg?.split("/")?.pop() || "")
                      || "https://dummyimage.com/250/ffffff/000000"
                    }
                    top
                  />
                  <ListGroup flush>
                    <ListGroupItem>
                      <ListGroupItemHeading>{pp.product.title}</ListGroupItemHeading>
                      <div className="float-right">
                        {pp.product.active ? (
                          <Badge color="success" pill>
                            Allowed
                          </Badge>
                        ) : (
                          <Badge color="danger" pill>
                            Disallowed
                          </Badge>
                        )}
                      </div>
                    </ListGroupItem>

                    <ListGroupItem>
                      <ListGroupItemHeading>Category</ListGroupItemHeading>
                      {SubCategoriesList.filter((sc) => sc._id === pp.subcategory).map((sc) => (
                        <ListGroupItemText key={sc._id}>{sc.category.name}</ListGroupItemText>
                      ))}
                    </ListGroupItem>

                    <ListGroupItem>
                      <ListGroupItemHeading>Sub Category</ListGroupItemHeading>
                      {SubCategoriesList.filter((sc) => sc._id === pp.subcategory).map((sc) => (
                        <ListGroupItemText key={sc._id}>{sc.name}</ListGroupItemText>
                      ))}
                    </ListGroupItem>

                    <ListGroupItem className="text-center">
                      {requiresChecker !== pp.product._id ? (
                        <button
                          type="button"
                          className="div-icon-animation"
                          data-testid={pp.product._id}
                          aria-label={`Select ${pp.product.title}`}
                          onClick={() => onClickproductsHandler(pp)}
                        >
                          <i className="ni ni-check-bold fa-3x text-muted" />
                        </button>
                      ) : (
                        <div>
                          <i className="ni ni-check-bold fa-3x text-green" />
                        </div>
                      )}
                    </ListGroupItem>
                  </ListGroup>
                </Card>
              </Col>
            ))}
          </Row>

          <GenericPagination
            currentPage={currentPageProducts}
            totalPages={totalProductPages}
            onPageChange={handleProductsPaginationClick}
          />

          {checker.product && !requiresChecker && (
            <div className="text-danger mb-3">
              {isValidationAttempted && "Please select a product."}
            </div>
          )}
        </>
      )}

      {/* POSTS PICKER */}
      {checker.posts && postsAvailability && (
        <>
          <Row>
            <h4 className="h3 text-info text-uppercase">Select Page post :</h4>
          </Row>

          <Row>
            <FormGroup className="navbar-search form-inline pt-4 mr-sm-3 navbar-search-dark  float-left">
              <FormGroup className="mb-0">
                <InputGroup className="input-group-alternative input-group-merge">
                  <InputGroupText>
                    <i className="fas fa-search" />
                  </InputGroupText>
                  <Input placeholder="Search for post.." type="text" onChange={searchPost} />
                </InputGroup>
              </FormGroup>
            </FormGroup>
          </Row>

          <Row>
            {slicedPosts.map((post, idx) => (
              <Col md="6" xl="3" key={post._id || idx}>
                <Card className="card-stats">
                  <CardBody>
                    <CardImg alt={post.title} src={post.picture} top />
                    <CardTitle tag="h5" className="text-uppercase text-muted mb-0">
                      {post.title}
                    </CardTitle>

                    {requiresChecker !== post._id ? (
                      <button
                        type="button"
                        className="div-icon-animation"
                        data-testid={post._id}
                        aria-label={`Select ${post.title}`}
                        onClick={() => onClickPostHandler(post)}
                      >
                        <i className="ni ni-check-bold fa-3x text-muted" />
                      </button>
                    ) : (
                      <div>
                        <i className="ni ni-check-bold fa-3x text-green" />
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Posts Pagination */}
          <CardHeader className="text-center">
            <Row>
              <nav aria-label="Page navigation example ">
                <Pagination className="pagination justify-content-center" listClassName="justify-content-center">
                  <PaginationItem disabled={currentPage <= 0}>
                    <PaginationLink href="#" onClick={(e) => handlePostsPageClick(e, currentPage - 1)} previous>
                      <i className="fa fa-angle-left" />
                      <span className="sr-only">Previous</span>
                    </PaginationLink>
                  </PaginationItem>

                  {[...Array(pagesCount)].map((_, i) => (
                    // Static list (SCRUM-2184): i is the page number itself, the item's identity.
                    <PaginationItem active={i === currentPage} key={i}>
                      <PaginationLink href="#" onClick={(e) => handlePostsPageClick(e, i)}>
                        {i + 1}
                      </PaginationLink>
                    </PaginationItem>
                  ))}

                  <PaginationItem disabled={currentPage >= pagesCount - 1}>
                    <PaginationLink href="#" onClick={(e) => handlePostsPageClick(e, currentPage + 1)} next>
                      <i className="fa fa-angle-right" />
                      <span className="sr-only">Next</span>
                    </PaginationLink>
                  </PaginationItem>
                </Pagination>
              </nav>
            </Row>
          </CardHeader>

          {checker.posts && !requiresChecker && (
            <div className="text-danger mb-2">
              {isValidationAttempted && "Please select a post."}
            </div>
          )}
        </>
      )}

      {/* AUTHORS PICKER */}
      {checker.authors && postsAvailability && (
        <>
          <Row>
            <h4 className="h3 text-info text-uppercase">Select Page author :</h4>
          </Row>
          <Row>
            {authors.map((author, index) => (
              <Col md="6" xl="3" key={author._id || index}>
                <Card>
                  <CardBody>
                    <a className="avatar avatar-xl rounded-circle" href="#" onClick={(e) => e.preventDefault()}>
                      <CardImg alt={author.name} src={author.avatar} top />
                    </a>
                    <CardTitle tag="h5" className="text-uppercase text-primary mb-0">
                      {author.name}
                    </CardTitle>
                    <ListGroup flush>
                      <ListGroupItem>
                        <ListGroupItemHeading>Posts</ListGroupItemHeading>
                        {author.posts.map((pt, i) => (
                          <div className="progress-label" key={`${author._id}_${i}`}>
                            <span>{pt}</span>
                            <br />
                          </div>
                        ))}
                      </ListGroupItem>
                    </ListGroup>
                  </CardBody>

                  {requiresChecker !== author._id ? (
                    <button
                      type="button"
                      className="div-icon-animation"
                      data-testid={author._id}
                      aria-label={`Select ${author.name}`}
                      onClick={() => onClickAuthorHandler(author)}
                    >
                      <i className="ni ni-check-bold fa-3x text-muted" />
                    </button>
                  ) : (
                    <div>
                      <i className="ni ni-check-bold fa-3x text-green" />
                    </div>
                  )}
                </Card>
              </Col>
            ))}
          </Row>

          {checker.authors && !requiresChecker && (
            <div className="text-danger mb-2">
              {isValidationAttempted && "Please select an author."}
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default PageTypesCreateView;
